/* DemoPool sample viewer: real pictures instead of initials.
   An element with data-img="<cand> <cand> …" gets the first candidate that loads, once it is on screen:
     url:<https image>   – direct (Ninja Digger image_url / playlists[].cover, YouTube video thumbnail)
     oe:<url>            – oEmbed thumbnail: Spotify playlist/artist, SoundCloud profile (free, no key, CORS *)
   oEmbed: at most 6 requests in flight, 5 s timeout, results cached in sessionStorage.
   Nothing loads → the element keeps its initials. */
(() => {
  const KEY = 'dp-img:', MAX = 6, TIMEOUT = 5000;
  const OE_IMG = /^https:\/\/[a-z0-9.-]+\.(spotifycdn\.com|scdn\.co|sndcdn\.com)\//i;
  const ss = {
    get: k => { try { return sessionStorage.getItem(k); } catch (_) { return null; } },
    set: (k, v) => { try { sessionStorage.setItem(k, v); } catch (_) {} }
  };
  const endpoint = u => {
    const sp = /open\.spotify\.com\/(?:intl-[a-z-]+\/)?(playlist|artist)\/([A-Za-z0-9]{22})/.exec(u);
    if (sp) return 'https://open.spotify.com/oembed?url=' + encodeURIComponent('https://open.spotify.com/' + sp[1] + '/' + sp[2]);
    const sc = /^https?:\/\/(?:www\.|m\.)?soundcloud\.com\/([^\/?#\s]+)\/?$/i.exec(u);
    if (sc) return 'https://soundcloud.com/oembed?format=json&url=' + encodeURIComponent('https://soundcloud.com/' + sc[1].toLowerCase());
    return '';
  };
  const mem = new Map(), queue = [];
  let inflight = 0;
  const pump = () => {
    while (inflight < MAX && queue.length) {
      const { ep, res } = queue.shift();
      inflight++;
      const ac = 'AbortController' in window ? new AbortController() : null;
      const t = setTimeout(() => ac && ac.abort(), TIMEOUT);
      fetch(ep, { credentials: 'omit', signal: ac ? ac.signal : undefined })
        .then(r => r.ok ? r.json() : Promise.reject(r.status))
        .then(j => {
          let u = j && j.thumbnail_url || '';
          if (!OE_IMG.test(u) || /default_avatar/i.test(u)) u = '';
          ss.set(KEY + ep, u); res(u);
        })
        .catch(st => { if (typeof st === 'number' && st < 500) ss.set(KEY + ep, ''); res(''); })
        .finally(() => { clearTimeout(t); inflight--; pump(); });
    }
  };
  const oembed = u => {
    const ep = endpoint(u);
    if (!ep) return Promise.resolve('');
    if (mem.has(ep)) return mem.get(ep);
    const c = ss.get(KEY + ep);
    const p = c !== null ? Promise.resolve(c) : new Promise(res => { queue.push({ ep, res }); pump(); });
    mem.set(ep, p);
    return p;
  };
  const resolve = cand => {
    const i = cand.indexOf(':'), type = cand.slice(0, i), v = cand.slice(i + 1);
    if (type === 'url') return Promise.resolve(/^https:\/\//i.test(v) ? { src: v, min: 16 } : null);
    if (type === 'oe') return oembed(v).then(src => src ? { src, min: 16 } : null);
    return Promise.resolve(null);
  };
  const tryLoad = (el, list) => {
    if (!list.length || !el.isConnected) return;
    resolve(list[0]).then(c => {
      if (!c) return tryLoad(el, list.slice(1));
      const img = new Image();
      img.alt = ''; img.decoding = 'async'; img.loading = 'lazy'; img.referrerPolicy = 'no-referrer';
      img.onload = () => {
        if (img.naturalWidth < c.min) { img.remove(); return tryLoad(el, list.slice(1)); }
        el.classList.add('has-img'); if (c.fav) el.classList.add('is-logo');
      };
      img.onerror = () => { img.remove(); tryLoad(el, list.slice(1)); };
      img.src = c.src;
      el.appendChild(img);
    });
  };
  const paint = el => {
    if (el.dataset.imgDone) return;
    el.dataset.imgDone = '1';
    tryLoad(el, (el.dataset.img || '').split(/\s+/).filter(Boolean));
  };
  const io = 'IntersectionObserver' in window ? new IntersectionObserver(es => es.forEach(e => { if (e.isIntersecting) { io.unobserve(e.target); paint(e.target); } }), { rootMargin: '150px' }) : null;
  const scan = root => root.querySelectorAll('[data-img]:not([data-img-done])').forEach(el => io ? io.observe(el) : paint(el));
  document.querySelectorAll('[data-prev]').forEach(box => {
    scan(box);
    new MutationObserver(() => scan(box)).observe(box, { childList: true, subtree: true });
  });

  /* "See 5 free" / "Try 10 free" on a product card: stay on the page, scroll to the sample and reload it with that card's rows.
     Cards (dp-builder-cards): data-g = all | g-<group> | <genre>. Volume cards (dp-builder-cards-vol, data-seg): data-g = all | <group>, plus the region switch. */
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const sliceOf = card => {
    const g = card.dataset.g || 'all';
    if (!('seg' in card.dataset)) return { slice: g, reg: '' };
    const root = card.closest('.ccards, .ccwrap, section') || document;
    const r = ((root.querySelector('input[name=ccreg]:checked') || {}).value || '').trim();
    const RG = { europe: 'eu', eu: 'eu', us: 'us', usa: 'us', 'united states': 'us' }, k = r.toLowerCase();
    const pre = r && !/^worldwide$/i.test(r) ? (RG[k] || k.replace(/[^a-z0-9]+/g, '-')) + '-' : '';
    return { slice: pre + (g === 'all' ? 'all' : 'g-' + g), reg: pre ? r : '' };
  };
  const chipFor = (box, text, onClear) => {
    const bar = box.querySelector('.macwin__bar'); if (!bar) return;
    let c = bar.querySelector('[data-smp-chip]');
    if (!text) { if (c) c.remove(); bar.classList.remove('has-chip'); return; }
    if (!c) { c = document.createElement('button'); c.type = 'button'; c.className = 'smp-chip'; c.setAttribute('data-smp-chip', ''); bar.insertBefore(c, bar.querySelector('.macwin__exp')); }
    c.innerHTML = '<span></span><b aria-hidden="true">×</b>';
    c.firstChild.textContent = text;
    c.setAttribute('aria-label', text + '. Show all genres');
    c.onclick = onClear;
    bar.classList.add('has-chip');
  };
  let clk = 0;
  document.addEventListener('click', e => {
    const a = e.target.closest('.ccard [data-sample]');
    const box = document.querySelector('[data-prev]');
    if (!a || !box || typeof box._sample !== 'function' || e.metaKey || e.ctrlKey || e.shiftKey) return;
    const card = a.closest('.ccard'), { slice, reg } = sliceOf(card), name = card.dataset.name || '';
    const reset = () => { chipFor(box, ''); box._sample('all'); };
    if (box._open) box._open();
    // 20.10 owner: Get 10 free goes straight to the email form (dp-collection.js); the preview only switches to this group
    const my = ++clk;
    box.classList.add('is-loading');
    box._sample(slice, 5, (card.closest('.ccards') || {}).dataset ? card.closest('.ccards').dataset.skey : '').then(n => {
      if (n < 0) return; // a newer click took over
      if (n) return chipFor(box, slice === 'all' ? '' : 'Showing: ' + name + (reg ? ' · ' + reg : ''), reset);
      chipFor(box, 'No sample for ' + name + ' yet · showing all', reset);
      return box._sample('all');
    }).finally(() => { if (my === clk) box.classList.remove('is-loading'); });
  });
  window.DPSample = { oembed };
})();
